// src/components/review/TriageConversationList.js
// Tab "Triage WhatsApp" do ReviewWorkbench — Conversation View (G-UX1, UX-2/3/4).
// A unidade de decisão do humano é a CONVERSA, não a mensagem (UX_FINDINGS §UX-2:
// 252 mensagens pendentes = 35 conversas). Substitui TriageMessageList (1 card/msg).
//
// Decisões de design:
//   - agrupamento client-side por coalesce(source_chat_label, telefone_contato)
//   - badge grupo/1:1 por heurística (UX-3): label presente e ≠ telefone ⇒ grupo/label
//   - expand → timeline data_envio asc com direção visual (entrada/saída) e tipo_midia
//   - ações na conversa inteira + seleção parcial de mensagens na timeline +
//     seleção múltipla de conversas (BulkActionBar)
//   - triage usa o mecanismo existente: UPDATE intake_status (sem RPC, sem seconds —
//     decided_in_seconds é métrica do EREL, nunca enviada em triage/lote, UX-1)
//   - onLoadedCount reporta a contagem real carregada para a página alinhar o badge
//     da tab à lista por construção (causa do 395≠252: snapshot stale, não filtro)

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Briefcase, X, ArrowDownLeft, ArrowUpRight, Image as ImageIcon,
  Video as VideoIcon, Mic, FileText, MapPin, Smile, Zap, AlertCircle,
  ChevronDown, ChevronUp, Users, User, MessageCircle,
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import useBulkSelection from '../shared/useBulkSelection';
import BulkActionBar from '../shared/BulkActionBar';
import SelectAllCheckbox from '../shared/SelectAllCheckbox';

const BULK_CHUNK = 50;

const MIDIA_META = {
  texto:       { label: null,           icon: null,       tone: '' }, // sem badge para texto
  imagem:      { label: 'imagem',       icon: ImageIcon,  tone: 'bg-purple-100 text-purple-700' },
  video:       { label: 'vídeo',        icon: VideoIcon,  tone: 'bg-rose-100   text-rose-700' },
  voz:         { label: 'voz',          icon: Mic,        tone: 'bg-amber-100  text-amber-800' },
  audio:       { label: 'áudio',        icon: Mic,        tone: 'bg-amber-100  text-amber-800' },
  documento:   { label: 'documento',    icon: FileText,   tone: 'bg-indigo-100 text-indigo-700' },
  localizacao: { label: 'localização',  icon: MapPin,     tone: 'bg-teal-100   text-teal-700' },
  contato:     { label: 'vCard',        icon: FileText,   tone: 'bg-cyan-100   text-cyan-700' },
  sticker:     { label: 'sticker',      icon: Smile,      tone: 'bg-pink-100   text-pink-700' },
  action:      { label: 'sistema',      icon: Zap,        tone: 'bg-slate-100  text-slate-600' },
};

function fmtDay(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function fmtHour(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function fmtPhone(raw) {
  if (!raw) return '';
  const d = String(raw).replace(/\D+/g, '');
  if (d.length === 11) return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
  if (d.length === 13 && d.startsWith('55')) {
    return `(${d.slice(2,4)}) ${d.slice(4,9)}-${d.slice(9)}`;
  }
  return raw;
}

// UX-3: label presente e distinto do telefone (comparação por dígitos) ⇒ grupo/label
function isGroupLike(label, telefone) {
  if (!label) return false;
  const labelDigits = String(label).replace(/\D+/g, '');
  const phoneDigits = String(telefone || '').replace(/\D+/g, '');
  if (labelDigits && phoneDigits && labelDigits === phoneDigits) return false;
  return true;
}

// Agrupa mensagens pendentes em conversas (chave = coalesce(label, telefone))
function groupConversations(messages) {
  const byKey = new Map();
  for (const m of messages) {
    const key = m.source_chat_label || m.telefone_contato || '(sem identificação)';
    if (!byKey.has(key)) {
      byKey.set(key, {
        key,
        label: m.source_chat_label || null,
        telefone: m.telefone_contato || null,
        messages: [],
      });
    }
    const conv = byKey.get(key);
    conv.messages.push(m);
    if (!conv.telefone && m.telefone_contato) conv.telefone = m.telefone_contato;
  }
  const convs = Array.from(byKey.values());
  for (const c of convs) {
    c.messages.sort((a, b) => String(a.data_envio || '').localeCompare(String(b.data_envio || '')));
    c.first = c.messages[0]?.data_envio || null;
    c.last = c.messages[c.messages.length - 1]?.data_envio || null;
    c.isGroup = isGroupLike(c.label, c.telefone);
  }
  // conversa com atividade mais recente primeiro
  convs.sort((a, b) => String(b.last || '').localeCompare(String(a.last || '')));
  return convs;
}

export default function TriageConversationList({ onChange, onLoadedCount }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [toast, setToast] = useState(null);
  const sel = useBulkSelection(); // seleção de CONVERSAS (por key)
  const toastTimerRef = useRef(null);

  const showToast = useCallback((message, tone = 'success') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message, tone });
    toastTimerRef.current = setTimeout(() => setToast(null), 2500);
  }, []);

  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('mensagens_whatsapp')
      .select('id, source_chat_label, telefone_contato, texto, data_envio, direcao, tipo_midia')
      .eq('intake_status', 'pending_triage')
      .order('data_envio', { ascending: true });

    if (err) {
      setError(err.message);
      setMessages([]);
    } else {
      setMessages(data || []);
      // alinha o badge da tab à contagem REAL carregada (mesma query da lista)
      if (typeof onLoadedCount === 'function') onLoadedCount((data || []).length);
    }
    setLoading(false);
  }, [onLoadedCount]);

  useEffect(() => {
    load();
  }, [load]);

  const conversations = useMemo(() => groupConversations(messages), [messages]);
  const convByKey = useMemo(
    () => new Map(conversations.map((c) => [c.key, c])),
    [conversations],
  );

  // Triage = UPDATE intake_status em chunks (mecanismo existente; sem RPC, sem seconds)
  const updateMessages = useCallback(async (ids, action) => {
    for (let i = 0; i < ids.length; i += BULK_CHUNK) {
      const chunk = ids.slice(i, i + BULK_CHUNK);
      const { error: updErr } = await supabase
        .from('mensagens_whatsapp')
        .update({ intake_status: action })
        .in('id', chunk);
      if (updErr) return updErr;
    }
    return null;
  }, []);

  const afterMutation = useCallback(async (okMessage, errObj) => {
    if (errObj) {
      showToast(`Erro ao salvar: ${errObj.message}`, 'error');
    } else if (okMessage) {
      showToast(okMessage, 'success');
    }
    await load(); // consistência > otimismo (mutações tocam N mensagens)
    if (!errObj && typeof onChange === 'function') onChange();
  }, [load, onChange, showToast]);

  // Ação na conversa inteira (ou em parte dela, via ids selecionados na timeline)
  const decideMessages = useCallback(async (convKey, ids, action, scopeLabel) => {
    if (!ids || ids.length === 0) return;
    setBusyKey(convKey);
    const errObj = await updateMessages(ids, action);
    setBusyKey(null);
    const one = ids.length === 1;
    const verb = action === 'auto_business'
      ? (one ? 'marcada como negócio' : 'marcadas como negócio')
      : (one ? 'ignorada' : 'ignoradas');
    await afterMutation(errObj ? null : `${ids.length} mensage${one ? 'm' : 'ns'} de ${scopeLabel} ${verb}`, errObj);
  }, [updateMessages, afterMutation]);

  // Lote de CONVERSAS selecionadas → todas as mensagens pendentes delas
  const bulkDecideConversations = async (action) => {
    const keys = sel.getSelectedIds();
    const ids = keys.flatMap((k) => (convByKey.get(k)?.messages || []).map((m) => m.id));
    if (ids.length === 0) return;
    const errObj = await updateMessages(ids, action);
    if (errObj) {
      showToast(`Erro ao salvar lote: ${errObj.message}`, 'error');
      await load();
      throw errObj; // BulkActionBar mantém seleção
    }
    const verb = action === 'auto_business' ? 'marcadas como negócio' : 'ignoradas';
    showToast(`${keys.length} conversa${keys.length === 1 ? '' : 's'} (${ids.length} mensagens) ${verb}`, 'success');
    await load();
    if (typeof onChange === 'function') onChange();
  };

  const headerLabel = loading
    ? 'Carregando…'
    : `${conversations.length} conversa${conversations.length === 1 ? '' : 's'} · ${messages.length} mensage${messages.length === 1 ? 'm' : 'ns'} pendente${messages.length === 1 ? '' : 's'}`;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-slate-600 font-medium">{headerLabel}</div>
        {conversations.length > 0 && (
          <SelectAllCheckbox
            totalCount={conversations.length}
            selectedCount={sel.count}
            onSelectAll={() => sel.selectAll(conversations.map((c) => c.key))}
            onDeselectAll={sel.deselectAll}
          />
        )}
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-xs text-red-800 flex items-start gap-2">
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && conversations.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
          <p className="text-sm text-slate-600">Nenhuma conversa pendente para triage.</p>
        </div>
      )}

      {conversations.map((c) => (
        <ConversationCard
          key={c.key}
          conversation={c}
          busy={busyKey === c.key}
          selected={sel.isSelected(c.key)}
          onToggleSelect={() => sel.toggle(c.key)}
          onDecide={(ids, action, scopeLabel) => decideMessages(c.key, ids, action, scopeLabel)}
        />
      ))}

      <BulkActionBar
        count={sel.count}
        onClear={sel.deselectAll}
        actions={[
          {
            label: 'Marcar como Negócio',
            icon: Briefcase,
            color: 'blue',
            confirmMessage: (n) =>
              `Classificar todas as mensagens pendentes de ${n} conversa${n === 1 ? '' : 's'} como negócio?`,
            onClick: () => bulkDecideConversations('auto_business'),
          },
          {
            label: 'Ignorar selecionadas',
            icon: X,
            color: 'gray',
            confirmMessage: (n) =>
              `Ignorar todas as mensagens pendentes de ${n} conversa${n === 1 ? '' : 's'}?`,
            onClick: () => bulkDecideConversations('ignored'),
          },
        ]}
      />

      {toast && (
        <div
          className={`fixed bottom-4 right-4 z-50 px-3 py-2 rounded shadow-lg text-xs font-medium ${
            toast.tone === 'error' ? 'bg-red-600 text-white' : 'bg-emerald-600 text-white'
          }`}
          role="status"
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

function ConversationCard({ conversation, busy, selected, onToggleSelect, onDecide }) {
  const [expanded, setExpanded] = useState(false);
  // seleção PARCIAL de mensagens dentro da timeline (UX-2: decidir parte da conversa)
  const [msgIds, setMsgIds] = useState(() => new Set());

  const c = conversation;
  const title = c.label || fmtPhone(c.telefone) || c.key;
  const samePeriodDay = c.first && c.last && fmtDay(c.first) === fmtDay(c.last);
  const periodo = samePeriodDay ? fmtDay(c.first) : `${fmtDay(c.first)} → ${fmtDay(c.last)}`;

  const toggleMsg = (id) => {
    setMsgIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const decideWhole = (action) => {
    const n = c.messages.length;
    const verb = action === 'auto_business' ? 'como negócio' : 'como ignoradas';
    if (!window.confirm(`Classificar ${n} mensage${n === 1 ? 'm' : 'ns'} de "${title}" ${verb}?`)) return;
    onDecide(c.messages.map((m) => m.id), action, `"${title}"`);
  };

  const decidePartial = (action) => {
    const ids = Array.from(msgIds);
    if (ids.length === 0) return;
    onDecide(ids, action, `"${title}"`);
    setMsgIds(new Set());
  };

  // separadores de data na timeline
  let lastDay = null;

  return (
    <div
      className={`bg-white border rounded-lg transition-opacity ${
        selected ? 'border-blue-400 bg-blue-50/40' : 'border-slate-200'
      } ${busy ? 'opacity-50 pointer-events-none' : ''}`}
    >
      {/* Header da conversa */}
      <div className="p-4 flex items-start gap-3">
        <input
          type="checkbox"
          checked={!!selected}
          onChange={onToggleSelect}
          aria-label="Selecionar conversa"
          className="cursor-pointer w-5 h-5 mt-0.5 accent-blue-600 flex-shrink-0 rounded"
        />
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex-1 min-w-0 text-left"
          aria-expanded={expanded}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-slate-900 truncate">{title}</span>
            <span
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${
                c.isGroup
                  ? 'bg-violet-50 text-violet-700 border-violet-200'
                  : 'bg-slate-50 text-slate-600 border-slate-200'
              }`}
              title={c.isGroup
                ? 'Heurística UX-3: label presente e distinto do telefone ⇒ provável grupo/label nomeado'
                : 'Conversa direta (label ausente ou igual ao telefone)'}
            >
              {c.isGroup ? <Users size={10} /> : <User size={10} />}
              {c.isGroup ? 'grupo' : '1:1'}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-1 text-[11px] text-slate-500">
            {c.telefone && <span className="font-mono">{fmtPhone(c.telefone)}</span>}
            <span className="inline-flex items-center gap-1">
              <MessageCircle size={10} />
              {c.messages.length} pendente{c.messages.length === 1 ? '' : 's'}
            </span>
            <span>·</span>
            <span>{periodo}</span>
          </div>
        </button>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex-shrink-0 mt-1 text-slate-400 hover:text-slate-600"
          aria-label={expanded ? 'Recolher conversa' : 'Expandir conversa'}
        >
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {/* Timeline (data_envio asc, direção visual, tipo_midia) — UX-4 */}
      {expanded && (
        <div className="px-4 pb-3 space-y-1 border-t border-slate-100 pt-3">
          {c.messages.map((m) => {
            const day = fmtDay(m.data_envio);
            const showDay = day !== lastDay;
            lastDay = day;
            return (
              <React.Fragment key={m.id}>
                {showDay && (
                  <div className="flex items-center gap-2 py-1">
                    <div className="flex-1 h-px bg-slate-100" />
                    <span className="text-[10px] text-slate-400 font-medium">{day}</span>
                    <div className="flex-1 h-px bg-slate-100" />
                  </div>
                )}
                <TimelineMessage
                  message={m}
                  selected={msgIds.has(m.id)}
                  onToggleSelect={() => toggleMsg(m.id)}
                />
              </React.Fragment>
            );
          })}

          {/* Ações parciais (mensagens selecionadas na timeline) */}
          {msgIds.size > 0 && (
            <div className="flex items-center justify-between gap-2 mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
              <span className="text-[11px] text-blue-800 font-medium">
                {msgIds.size} de {c.messages.length} selecionada{msgIds.size === 1 ? '' : 's'}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => decidePartial('ignored')}
                  className="px-2 py-1 text-[11px] font-medium text-slate-700 bg-white border border-slate-300 rounded hover:bg-slate-100 flex items-center gap-1"
                >
                  <X size={11} /> Ignorar
                </button>
                <button
                  type="button"
                  onClick={() => decidePartial('auto_business')}
                  className="px-2 py-1 text-[11px] font-medium text-white bg-blue-600 rounded hover:bg-blue-700 flex items-center gap-1"
                >
                  <Briefcase size={11} /> Negócio
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Ações na conversa inteira */}
      <div className="flex items-center justify-end gap-2 px-4 py-2 border-t border-slate-100">
        <button
          type="button"
          onClick={() => decideWhole('ignored')}
          disabled={busy}
          className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 flex items-center gap-1.5"
        >
          <X size={13} /> Ignorar conversa
        </button>
        <button
          type="button"
          onClick={() => decideWhole('auto_business')}
          disabled={busy}
          className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
        >
          <Briefcase size={13} /> Negócio (conversa)
        </button>
      </div>
    </div>
  );
}

function TimelineMessage({ message, selected, onToggleSelect }) {
  const m = message;
  const isEntrada = m.direcao === 'entrada';
  const DirIcon = isEntrada ? ArrowDownLeft : ArrowUpRight;
  const midia = MIDIA_META[m.tipo_midia] || {
    label: m.tipo_midia, icon: FileText, tone: 'bg-slate-100 text-slate-600',
  };
  const MidiaIcon = midia.icon;

  return (
    <div className={`flex items-start gap-2 ${isEntrada ? '' : 'flex-row-reverse'}`}>
      <input
        type="checkbox"
        checked={!!selected}
        onChange={onToggleSelect}
        aria-label="Selecionar mensagem"
        className="cursor-pointer w-3.5 h-3.5 mt-2 accent-blue-600 flex-shrink-0 rounded"
      />
      <div
        className={`max-w-[80%] px-3 py-1.5 rounded-lg border text-xs leading-snug ${
          isEntrada
            ? 'bg-white border-slate-200 text-slate-800'
            : 'bg-emerald-50 border-emerald-200 text-slate-800'
        } ${selected ? 'ring-1 ring-blue-400' : ''}`}
      >
        <div className={`flex items-center gap-1.5 mb-0.5 text-[10px] text-slate-400 ${isEntrada ? '' : 'justify-end'}`}>
          <DirIcon size={9} className={isEntrada ? 'text-blue-500' : 'text-emerald-600'} />
          <span>{fmtHour(m.data_envio)}</span>
          {midia.label && (
            <span className={`inline-flex items-center gap-0.5 px-1 py-0 rounded font-medium ${midia.tone}`}>
              {MidiaIcon && <MidiaIcon size={9} />} {midia.label}
            </span>
          )}
        </div>
        <p className="whitespace-pre-wrap break-words">
          {m.texto || <span className="italic text-slate-400">(sem texto)</span>}
        </p>
      </div>
    </div>
  );
}
